import LazyHydrate from 'react-lazy-hydration'
import { ResponsiveObject } from 'react-slick'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { ExperimentVariation, GBFadeInExperiment } from '@/experimentation'
import { PortraitRowCollection } from '@/molecules/PortraitRowCollection'
import { findTitleProjectSlug, formatTitleLikeVerticalImagePath, formatWatchPath } from '@/services/ContentCatalog'
import { useGuildMember } from '@/services/GuildUserService'
import { CatalogTitleForMap, LinkViewModel, LinksViewProps } from '@/services/RenderService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { logger } from '@/utils/logging'
import { LargePortraitLink } from './LargePortraitLink'
import { NumberedPosterLink } from './NumberedPosterLink'

export function LargePortraitRail(props: LinksViewProps) {
  const { isGuildMember } = useGuildMember()
  const testId = `large-portrait-rail-${props.id}`

  const EXPERIMENT_COLLECTION_IDS = [
    '2shbay5yTskPONGpQ3Q2sG',
    '4xlxq4jcyjtUoleGQNVn2m',
    '7Cwts1DFTHgjvDQjLPOuQG',
    '5BjiiG7NEEPMqKlNnc1HAS',
    '2vWDwqopG625WIDNO9hiUa',
    '82bz51AOW0fIRNQRcYWKs',
  ]

  const isExperimentCollection = EXPERIMENT_COLLECTION_IDS.includes(props.id)

  const variantSlickResponsiveObject: ResponsiveObject[] = [
    {
      breakpoint: 1536,
      settings: {
        slidesToScroll: 4,
        slidesToShow: 4,
      },
    },
    {
      breakpoint: 1280,
      settings: {
        slidesToScroll: 3,
        slidesToShow: 3,
      },
    },
    {
      breakpoint: 1024,
      settings: {
        slidesToScroll: 3,
        slidesToShow: 3,
      },
    },
    {
      breakpoint: 768,
      settings: {
        slidesToScroll: 2,
        slidesToShow: 2,
      },
    },
    {
      breakpoint: 640,
      settings: {
        slidesToScroll: 2,
        slidesToShow: 2,
      },
    },
    {
      breakpoint: 480,
      settings: {
        slidesToScroll: 2,
        slidesToShow: 2,
      },
    },
  ]

  return (
    <LazyHydrate whenVisible>
      <PaddedContainer className="-mt-2 overflow-x-hidden pt-2" fluid>
        {isExperimentCollection ? (
          <GBFadeInExperiment experimentName="top-10-numbered-design" defaultValue={false}>
            <ExperimentVariation variation={false}>
              <PortraitRowCollection id={props.id} testId={testId} slidesToScroll={6} slidesToShow={6} haltLazyLoad>
                {props.links.map((link) => (
                  <LargePortraitLink
                    key={link.name}
                    link={link}
                    className="mb-2 mr-4 max-h-full overflow-hidden"
                    isGuildMember={isGuildMember}
                  />
                ))}
              </PortraitRowCollection>
            </ExperimentVariation>
            <ExperimentVariation variation={true}>
              <PortraitRowCollection
                testId={testId}
                slidesToScroll={4}
                slidesToShow={4}
                haltLazyLoad
                responsiveSlick={variantSlickResponsiveObject}
                {...props}
              >
                {props.links.map((link, index) => (
                  <NumberedPosterLink key={link.name} link={link} isGuildMember={isGuildMember} rank={index + 1} />
                ))}
              </PortraitRowCollection>
            </ExperimentVariation>
          </GBFadeInExperiment>
        ) : (
          <PortraitRowCollection id={props.id} testId={testId} slidesToScroll={6} slidesToShow={6} haltLazyLoad>
            {props.links.map((link) => (
              <LargePortraitLink
                key={link.name}
                link={link}
                className="mb-2 mr-4 max-h-full overflow-hidden"
                isGuildMember={isGuildMember}
              />
            ))}
          </PortraitRowCollection>
        )}
      </PaddedContainer>
    </LazyHydrate>
  )
}

LargePortraitRail.mapTitle = mapTitleToLink

export function mapTitleToLink(title: CatalogTitleForMap): LinkViewModel | undefined {
  const path = formatTitleLikeVerticalImagePath(title)
  const imageUrl = path && getCloudinaryImageUrl({ path, width: 294, height: 441 })
  const linkUrl = formatWatchPath(title)

  if (!imageUrl || !linkUrl) {
    logger().error(
      `A link view model cannot be created for this title because one or both of imageUrl and linkUrl could not be generated.`,
      { component: LargePortraitRail.name, title, imageUrl, linkUrl },
    )
    return
  }

  return {
    name: title.title,
    alt: title.title,
    imageUrl,
    linkUrl,
    track: {
      eventName: 'Project Tile Clicked',
      payload: {
        name: title.title,
        slug: findTitleProjectSlug(title),
        label: title.description.short,
        tileType: 'large_portrait_rail',
        imageUrl,
        linkUrl,
      },
    },
  }
}
